import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useAppDispatch } from "../../../../redux/useDispatch";
import {
  landlordFirstNameChanged,
  landlordLastNameChanged,
  landlordEmailChanged,
  landlordPhoneChanged,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";

/**
 * Custom hook to hydrate host/landlord details from URL query parameters.
 *
 * @remarks
 * - Hydrates landlord first name, last name, email, and phone from URL
 * - Only runs once per mount to prevent overwriting user input
 * - Returns hydration status for loading states
 * - Follows the same pattern as useLocationHydration and useServiceHydration
 *
 * @returns Boolean indicating whether hydration is complete
 *
 * @example
 * ```tsx
 * function HostDetailsPage() {
 *   const hydrated = useHostDetailsHydration();
 *   if (!hydrated) return <Loading />;
 *   return <Content />;
 * }
 * ```
 */
export function useHostDetailsHydration(): boolean {
  const location = useLocation();
  const dispatch = useAppDispatch();

  const hydratedRef = useRef(false);
  const capturedRef = useRef(false);
  const initialParamsRef = useRef<{
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
  } | null>(null);

  // Capture initial query parameters once on mount
  useEffect(() => {
    if (capturedRef.current) return;

    const params = new URLSearchParams(location.search);
    initialParamsRef.current = {
      firstName: params.get("landlordFirstName"),
      lastName: params.get("landlordLastName"),
      email: params.get("landlordEmail"),
      phone: params.get("landlordPhone"),
    };
    capturedRef.current = true;
  }, [location.search]);

  // Reset hydration when URL changes (user navigated back and changed values)
  useEffect(() => {
    return () => {
      hydratedRef.current = false;
      capturedRef.current = false;
      initialParamsRef.current = null;
    };
  }, [location.pathname]); // Reset when pathname changes

  // Hydrate from captured URL parameters
  useEffect(() => {
    if (hydratedRef.current) return;
    if (initialParamsRef.current === null) return;

    const { firstName, lastName, email, phone } = initialParamsRef.current;

    // Dispatch all hydration updates
    if (firstName) {
      dispatch(landlordFirstNameChanged({ firstName }));
    }

    if (lastName) {
      dispatch(landlordLastNameChanged({ lastName }));
    }

    if (email) {
      dispatch(landlordEmailChanged({ email }));
    }

    if (phone) {
      dispatch(landlordPhoneChanged({ phone }));
    }

    // Mark as hydrated even if no params were present
    hydratedRef.current = true;
  }, [dispatch]);

  return hydratedRef.current;
}
